/**
 * Pannello di trasferimento rapido v4 (N7, CT-60/CT-111/CT-121;
 * `Centro Ticket v4.dc.html` L608-L660 markup, L920-L936 logica).
 *
 * Superficie SOLO v4: le pagine la montano al posto di `TransferModal` quando
 * `useSupportLook() === 'v4'`; con la v3 resta la modale di sempre, che qui
 * NON si tocca. Replica il comportamento di `TransferModal` + dei suoi
 * chiamanti (`SupportTicketView.doTransfer`, `SupportCenterV2.doRowTransfer`):
 * - canale attuale escluso, solo i canali passati dal chiamante (quelli ammessi);
 * - destinazione preselezionata facoltativa (`''` = nessuna, come Proprietà
 *   → `onOpenTransfer('')`), sempre modificabile;
 * - motivo facoltativo, inviato con `trim()`;
 * - `busy`: niente doppio invio, Escape e clic sul velo non chiudono;
 * - errori via `handleSupportActionError` (409 → avviso + `onChanged`);
 * - successo: toast «Ticket trasferito», chiusura, poi `onTransferred`.
 * In più: anteprima del ticket SENZA segnarlo come letto (`getTicketPreview`
 * = `GET /:id?markRead=0`) e carico del canale da `getDispatchSummary`.
 *
 * Esc, focus iniziale/di ritorno, trappola del Tab e blocco dello scroll sono
 * quelli di `S3Drawer` (stack condiviso `layerStack.ts`): una modale aperta
 * sopra vince, il pannello sotto non ruba il tasto.
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { S3Badge, S3Drawer } from '../v3/primitives';
import { handleSupportActionError } from '../v3/SupportTicketView';
import { originVisual, requesterTypeVisual, slaBadge } from '../v3/visual';
import { relativeAge } from '../v3/queueModel';
import { classifySla } from '../../../utils/supportSla';
import { useToast } from '../../../contexts/ToastContext';
import { channelTone } from './channelPalette';
import ChannelSwatch from './ChannelSwatch';
import { hasChannelAvatar } from '../channelAvatar';
import CopyGroupsPicker from '../CopyGroupsPicker';
import { copyGroupNames, copyGroupOptions, currentCopyIds, transferArgs } from '../copyGroupsModel';
import type {
  CopyGroupRef,
  DispatchSummary,
  GroupLite,
  TicketDetail,
  TicketListItem,
} from '../../../services/supportApi';
// Il pannello porta da se `.sup3 .sup4` (token e classi s3-*): puo essere
// montato fuori dalla radice della pagina, come le modali. Il foglio v4 arriva
// da `lookContext` (unica sede dell'import, via primitives).
import '../../../styles/admin-v2.css';
import '../../../styles/support-v3.css';

/** Sottoinsieme dell'API operatore che serve al pannello. */
export interface QuickTransferApi {
  transfer: (id: string, toGroupId: string, motivo: string, copyGroupIds?: string[]) => Promise<void>;
  /** Anteprima senza segnare come letto (`adminSupportApi.getTicketPreview`). Facoltativa. */
  getTicketPreview?: (id: string) => Promise<TicketDetail>;
  /** Carico per canale «N in coda · N comp.» (`adminSupportApi.getDispatchSummary`). Facoltativa. */
  getDispatchSummary?: () => Promise<DispatchSummary>;
}

/** Il ticket come arriva dalla coda o dal dettaglio: bastano i campi della riga. */
export type QuickTransferTicket = Pick<
  TicketListItem,
  '_id' | 'ticketNumber' | 'subject' | 'status' | 'createdAt' | 'firstResponseAt' | 'sla' | 'origin'
> & {
  groupId?: { _id?: string; nome: string };
  requester?: { type: string; nome?: string; email?: string };
  associateSnapshot?: { nominativo?: string } | null;
  /** Canali in copia (parte A): dal dettaglio o dalla riga della coda. */
  copyGroups?: CopyGroupRef[];
  copyGroupIds?: string[];
};

export interface QuickTransferSheetProps {
  ticket: QuickTransferTicket;
  /** Canali ammessi (come `TransferModal.groups`); quello attuale viene escluso qui. */
  groups: GroupLite[];
  api: QuickTransferApi;
  /** Destinazione preselezionata (drag & drop, Proprietà). `''`/assente = nessuna. */
  initialGroupId?: string;
  onClose: () => void;
  /** Dopo il successo (il pannello si e gia chiuso): ricarica coda/scheda. */
  onTransferred?: (ticketId: string, toGroupId: string) => void | Promise<void>;
  /**
   * `false`: niente toast «Ticket trasferito», lo emette `onTransferred` (che sa
   * se il ticket e uscito dal perimetro: «Ticket trasferito a …»). Default true.
   */
  announce?: boolean;
  /** Ricarica sul 409 (stessa callback `onChanged` delle azioni del ticket). */
  onChanged: () => void | Promise<void>;
  /** «Apri ticket completo →»: assente = link nascosto. */
  onOpenTicket?: (ticketId: string) => void;
  /** Mostra «Anche in copia a» (parte A). Assente = comportamento di prima. */
  allowCopies?: boolean;
}

export const QUICK_TRANSFER_TITLE = 'Trasferisci a un canale';

/** Testo del bottone di conferma (L933). */
export const quickTransferButtonLabel = (target?: GroupLite | null): string =>
  target ? `Trasferisci a ${target.nome}` : 'Scegli un canale';

/** Carico del canale (L928): «N in coda · N comp.»; null se il canale non e nel riassunto. */
export const channelLoadLabel = (summary: DispatchSummary | null, groupId: string): string | null => {
  const ch = summary?.channels.find((c) => c.groupId === groupId);
  if (!ch) return null;
  return `${ch.notTaken} in coda · ${ch.memberCount} comp.`;
};

const requesterLabel = (t: QuickTransferTicket | TicketDetail): string =>
  t.requester?.nome || t.associateSnapshot?.nominativo || t.requester?.email || '—';

const QuickTransferSheet: React.FC<QuickTransferSheetProps> = ({
  ticket,
  groups,
  api,
  initialGroupId,
  onClose,
  onTransferred,
  announce = true,
  onChanged,
  onOpenTicket,
  allowCopies = false,
}) => {
  const toast = useToast();
  const currentId = ticket.groupId?._id || '';
  // Un canale disattivato non e mai una destinazione (come `transfer` lato server).
  const targets = useMemo(
    () => groups.filter((g) => g._id !== currentId && g.isActive !== false),
    [groups, currentId]
  );
  const [groupId, setGroupId] = useState<string>(() =>
    initialGroupId && targets.some((g) => g._id === initialGroupId) ? initialGroupId : ''
  );
  const [motivo, setMotivo] = useState<string>('');
  const [copies, setCopies] = useState<string[]>([]);
  const existingCopies = currentCopyIds(ticket);
  const [busy, setBusy] = useState<boolean>(false);
  const [preview, setPreview] = useState<TicketDetail | null>(null);
  const [previewState, setPreviewState] = useState<'loading' | 'ready' | 'error' | 'none'>(
    api.getTicketPreview ? 'loading' : 'none'
  );
  const [summary, setSummary] = useState<DispatchSummary | null>(null);
  const tilesRef = useRef<HTMLDivElement>(null);
  // Id univoci: due pannelli nella stessa pagina non collidono.
  const uid = useId();
  const destId = `s4-xfer-dest-${uid}`;
  const motivoId = `s4-xfer-motivo-${uid}`;

  const ticketId = ticket._id;
  const { getTicketPreview, getDispatchSummary } = api;

  useEffect(() => {
    if (!getTicketPreview) return;
    let alive = true;
    setPreviewState('loading');
    getTicketPreview(ticketId)
      .then((d) => { if (alive) { setPreview(d); setPreviewState('ready'); } })
      .catch(() => { if (alive) setPreviewState('error'); });
    return () => { alive = false; };
  }, [getTicketPreview, ticketId]);

  useEffect(() => {
    if (!getDispatchSummary) return;
    let alive = true;
    getDispatchSummary()
      .then((s) => { if (alive) setSummary(s); })
      .catch(() => { /* il carico e un di piu: senza, le tessere mostrano solo il nome */ });
    return () => { alive = false; };
  }, [getDispatchSummary]);

  // Come TransferModal: in volo Escape e il velo non chiudono; Annulla si.
  const requestClose = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);

  const target = targets.find((g) => g._id === groupId) || null;
  // Copie proponibili: tutti i canali attivi tranne smistamento, destinazione e copie esistenti.
  const copyOptions = allowCopies ? copyGroupOptions(groups, { responsibleId: target?._id, exclude: existingCopies }) : [];
  const chosen = copies.filter((id) => copyOptions.some((g) => g._id === id));
  // La destinazione non è mai anche in copia: se viene scelta, esce dalle copie (come TransferModal).
  const pickTarget = (id: string) => {
    setGroupId(id);
    setCopies((c) => c.filter((x) => x !== id));
  };

  const submit = async () => {
    if (busy || !target) return;
    setBusy(true);
    try {
      await api.transfer(ticketId, target._id, motivo.trim(), ...transferArgs(chosen));
      if (announce) toast.success('Ticket trasferito');
      onClose();
      await onTransferred?.(ticketId, target._id);
    } catch (err: any) {
      await handleSupportActionError(err, 'Errore trasferimento', toast, onChanged);
    } finally {
      setBusy(false);
    }
  };

  // Tessere = radiogroup: frecce/Home/End spostano focus e scelta (come i radio nativi).
  const onTilesKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(e.key) || targets.length === 0) return;
    e.preventDefault();
    const tiles = Array.from(tilesRef.current?.querySelectorAll<HTMLElement>('[role="radio"]') || []);
    const focused = tiles.indexOf(document.activeElement as HTMLElement);
    const i = focused >= 0 ? focused : Math.max(0, targets.findIndex((g) => g._id === groupId));
    const n = targets.length;
    const next =
      e.key === 'Home' ? 0
        : e.key === 'End' ? n - 1
          : e.key === 'ArrowRight' || e.key === 'ArrowDown' ? (i + 1) % n
            : (i - 1 + n) % n;
    pickTarget(targets[next]._id);
    tiles[next]?.focus();
  };

  const source = preview || ticket;
  const sla = slaBadge(classifySla(source, new Date()));
  const current = channelTone(ticket.groupId ? { _id: currentId, nome: ticket.groupId.nome } : null);
  const reqType = requesterTypeVisual(source.requester?.type);
  const origin = originVisual(source.origin);
  const firstMessage = preview?.publicThread?.[0]?.body || '';

  return (
    <div className="sup3 sup4 s4-xfer-host">
      <S3Drawer open onClose={requestClose} title={QUICK_TRANSFER_TITLE} className="s4-xfer-sheet" headClassName="s4-xfer__head">
        <div className="s4-xfer__kicker">TRASFERIMENTO RAPIDO · {ticket.ticketNumber}</div>

        <div className="s4-xfer__body">
          <section
            className="s4-xfer__card"
            aria-label="Anteprima del ticket"
            style={{ ['--s4-xfer-ch' as string]: `var(${current.cssVar})`, ['--s4-xfer-ch-fg' as string]: `var(${current.cssVar}-fg)` } as React.CSSProperties}
          >
            <div className="s4-xfer__row">
              <span className="s4-xfer__now">ORA IN · {ticket.groupId?.nome || '—'}</span>
              <span style={{ flex: 1 }} />
              {sla && <S3Badge tone={sla.tone} small title={sla.title}>{sla.label}</S3Badge>}
            </div>
            <div className="s4-xfer__subj">{source.subject}</div>
            <div className="s4-xfer__meta">
              <span>{requesterLabel(source)}</span>
              {reqType.label && <S3Badge tone={reqType.tone} small>{reqType.label}</S3Badge>}
              <span className="s4-xfer__fnt">
                {origin.label ? `· ${origin.label} ` : ''}· {relativeAge(source.createdAt)}
              </span>
            </div>
            {previewState === 'loading' && <div className="s4-xfer__msg s4-xfer__msg--muted">Caricamento anteprima…</div>}
            {previewState === 'error' && <div className="s4-xfer__msg s4-xfer__msg--muted">Anteprima non disponibile</div>}
            {previewState === 'ready' && (
              <div className="s4-xfer__msg">{firstMessage || 'Nessun messaggio'}</div>
            )}
          </section>

          <div>
            <div className="s4-xfer__label" id={destId}>CANALE DI DESTINAZIONE</div>
            {targets.length === 0 ? (
              <p className="s3-mod__note">Nessun altro canale disponibile.</p>
            ) : (
              <div
                ref={tilesRef}
                className="s4-xfer__tiles"
                role="radiogroup"
                aria-labelledby={destId}
                onKeyDown={onTilesKey}
              >
                {targets.map((g, i) => {
                  const tone = channelTone(g);
                  const on = g._id === groupId;
                  const load = channelLoadLabel(summary, g._id);
                  return (
                    <button
                      key={g._id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      tabIndex={on || (!groupId && i === 0) ? 0 : -1}
                      className={`s4-xfer__tile${on ? ' s4-xfer__tile--on' : ''}`}
                      style={{ ['--s4-xfer-tile' as string]: `var(${tone.cssVar})`, ['--s4-xfer-tile-soft' as string]: `var(${tone.cssVar}-soft)` } as React.CSSProperties}
                      onClick={() => pickTarget(g._id)}
                    >
                      <span className="s4-xfer__tilehead">
                        {hasChannelAvatar(g) ? (
                          <ChannelSwatch channel={g} size={22} sigla />
                        ) : (
                          <span className="s4-xfer__sigla" aria-hidden="true">{tone.sigla}</span>
                        )}
                        <span className="s4-xfer__tilename">{g.nome}</span>
                        {on && <Check size={14} className="s4-xfer__check" aria-hidden="true" />}
                      </span>
                      {load && <span className="s4-xfer__load">{load}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {allowCopies && (
            <CopyGroupsPicker
              options={copyOptions}
              value={chosen}
              onChange={setCopies}
              disabled={busy}
              already={copyGroupNames(existingCopies.filter((id) => id !== groupId), groups, ticket.copyGroups)}
            />
          )}

          <div className="s3-field">
            <label className="s4-xfer__label" htmlFor={motivoId}>MOTIVO (FACOLTATIVO)</label>
            <textarea
              id={motivoId}
              className="s3-ta"
              rows={2}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Indica il motivo del trasferimento…"
            />
          </div>
        </div>

        <div className="s4-xfer__foot">
          {onOpenTicket && (
            <button type="button" className="s4-xfer__open" onClick={() => onOpenTicket(ticketId)}>
              Apri ticket completo →
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button type="button" className="s3-pill" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="s3-pill s3-pill--primary"
            onClick={submit}
            disabled={busy || !target}
            aria-busy={busy || undefined}
          >
            {busy && <Loader2 size={14} className="s3-spin" />}
            {quickTransferButtonLabel(target)}
          </button>
        </div>
      </S3Drawer>
    </div>
  );
};

export default QuickTransferSheet;
